import { ref, computed, provide, inject, watch } from "vue"
import { darkTheme, type GlobalThemeOverrides } from "naive-ui"
import { PRESET_PALETTES, type Palette } from "@/themes/presets"
import { makeThemeOverrides } from "@/themes"
import { getReadableTextColorAA } from "@/utils/colors"
import { api } from "@/lib/api"
import { error as logError } from "@/utils/debug"
import { user } from "@/composables/auth/useAuth"

const KEY = Symbol("theme-palette")

export type ThemeState = {
  isDark: boolean
  setDark: (v: boolean) => void

  palettes: Palette[]
  addUserPalette: (p: Palette) => void

  currentKey: string
  setCurrentKey: (k: string) => Promise<void>
  loadThemeFromProfile: (themeKey: string | null) => void

  theme: any | null
  themeOverrides: GlobalThemeOverrides
}

function loadPersisted<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function persist<T>(key: string, value: T) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {}
}

export function provideThemePalette() {
  const stored = loadPersisted("theme-state", {
    isDark: false,
    currentKey: PRESET_PALETTES[0]?.key,
    userPalettes: [] as Palette[],
  })

  const isDark = ref<boolean>(stored.isDark)
  const currentKey = ref<string>(stored.currentKey as string)
  const userPalettes = ref<Palette[]>(stored.userPalettes || [])

  const palettes = computed<Palette[]>(() => [...PRESET_PALETTES, ...userPalettes.value])

  const current = computed<Palette>(() => {
    return palettes.value.find((p) => p.key === currentKey.value) ?? (PRESET_PALETTES[0] as Palette)
  })

  const theme = computed(() => (isDark.value ? darkTheme : null))
  const themeOverrides = computed(() => makeThemeOverrides(current.value))

  watch(
    () => current.value,
    (p) => {
      applyCssVars(p)
    },
    { immediate: true },
  )

  function setDark(v: boolean) {
    isDark.value = v
    persistState()
  }
  async function setCurrentKey(k: string) {
    currentKey.value = k

    // If user is authenticated, save to database
    if (user.value) {
      try {
        await api("updateProfile", { theme: k })
      } catch (error) {
        logError("Failed to save theme to database:", error)
      }
    }

    // Always persist to localStorage as fallback
    persistState()
  }
  function addUserPalette(p: Palette) {
    // ensure unique key
    if (!userPalettes.value.some((up) => up.key === p.key)) {
      userPalettes.value.push(p)
      persistState()
    }
  }

  function loadThemeFromProfile(themeKey: string | null) {
    if (themeKey && PRESET_PALETTES.some((p) => p.key === themeKey)) {
      currentKey.value = themeKey
      // Don't persist - we're loading from database
    }
  }

  function persistState() {
    persist("theme-state", {
      isDark: isDark.value,
      currentKey: currentKey.value,
      userPalettes: userPalettes.value,
    })
  }

  const _state: ThemeState = {
    isDark: isDark.value,
    setDark,
    palettes: palettes.value,
    addUserPalette,
    currentKey: currentKey.value,
    setCurrentKey,
    loadThemeFromProfile,
    theme: theme.value,
    themeOverrides: themeOverrides.value,
  }

  const reactiveState = {
    get isDark() {
      return isDark.value
    },
    setDark,
    get palettes() {
      return palettes.value
    },
    addUserPalette,
    get currentKey() {
      return currentKey.value
    },
    setCurrentKey,
    loadThemeFromProfile,
    get theme() {
      return theme.value
    },
    get themeOverrides() {
      return themeOverrides.value
    },
  } as ThemeState

  provide(KEY, reactiveState)
  return reactiveState
}

export function useThemePalette() {
  const s = inject<ThemeState>(KEY)
  if (!s) throw new Error("useThemePalette must be used after provideThemePalette()")
  return s
}

function applyCssVars(palette: Palette) {
  const root = document.documentElement
  root.style.setProperty("--color-primary", palette.primary)
  root.style.setProperty("--color-info", palette.info)
  root.style.setProperty("--color-success", palette.success)
  root.style.setProperty("--color-warning", palette.warning)
  root.style.setProperty("--color-error", palette.error)

  if (palette.neutralBg) root.style.setProperty("--color-neutral-bg", palette.neutralBg)
  if (palette.neutralText) root.style.setProperty("--color-neutral-text", palette.neutralText)

  // Add accessible text colors based on backgrounds
  // These ensure AA contrast ratios for accessibility
  root.style.setProperty("--text-on-primary", getReadableTextColorAA(palette.primary))
  root.style.setProperty("--text-on-info", getReadableTextColorAA(palette.info))
  root.style.setProperty("--text-on-success", getReadableTextColorAA(palette.success))
  root.style.setProperty("--text-on-warning", getReadableTextColorAA(palette.warning))
  root.style.setProperty("--text-on-error", getReadableTextColorAA(palette.error))

  // Accessible text colors for neutral colors
  const neutralBg = palette.neutralBg || "#141414"
  const neutralText = palette.neutralText || "#F2F3F4"

  root.style.setProperty("--text-on-bg", getReadableTextColorAA(neutralBg))
  root.style.setProperty("--text-on-neutral-bg", getReadableTextColorAA(neutralBg))
  root.style.setProperty("--text-on-neutral-text", getReadableTextColorAA(neutralText))
}
